import { useState, useEffect } from 'react';
import axiosClient from './lib/axiosClient';

const STATUS_COLOR = {
  available:   { color: '#4ade80', label: 'Available' },
  rented:      { color: '#f87171', label: 'Rented' },
  maintenance: { color: '#fb923c', label: 'Maintenance' },
  repair:      { color: '#f87171', label: 'In Repair' },
  retired:     { color: '#555',    label: 'Retired' },
};

export default function ItemPickerModal({ onSelect, onClose, selectedVariantIds = [] }) {
  const [products, setProducts]       = useState([]);
  const [categories, setCategories]   = useState([]);
  const [loading, setLoading]         = useState(true);
  const [search, setSearch]           = useState('');
  const [expandedCats, setExpandedCats]   = useState({});
  const [expandedProds, setExpandedProds] = useState({});

  useEffect(() => {
    Promise.all([
      axiosClient.get('/api/rentals/products'),
      axiosClient.get('/api/rentals/categories'),
    ]).then(([pr, cr]) => {
      const prods = (pr.data.products || []).map(p => ({
        ...p, variants: p.rental_product_variants || p.variants || [],
      }));
      setProducts(prods);
      setCategories(cr.data.categories || []);
      const expanded = {};
      (cr.data.categories || []).filter(c => !c.parent_id).forEach(c => { expanded[c.id] = true; });
      setExpandedCats(expanded);
    }).finally(() => setLoading(false));
  }, []);

  const getRecommended = (variants) => {
    const avail = variants.filter(v => v.unit_status === 'available');
    if (!avail.length) return null;
    const score = { new: 5, excellent: 4, good: 3, fair: 2, poor: 1 };
    return [...avail].sort((a, b) =>
      ((score[b.condition]||3)*1000 - (b.hour_meter||0)) -
      ((score[a.condition]||3)*1000 - (a.hour_meter||0))
    )[0];
  };

  const searchLower = search.toLowerCase().trim();
  const filteredProducts = searchLower ? products.filter(p => {
    const catName = categories.find(c => c.id === p.category_id)?.name || '';
    return p.name?.toLowerCase().includes(searchLower)
      || catName.toLowerCase().includes(searchLower)
      || (p.variants||[]).some(v =>
          (v.serial_number||'').toLowerCase().includes(searchLower) ||
          (v.sku||'').toLowerCase().includes(searchLower));
  }) : null;

  const rootCats   = categories.filter(c => !c.parent_id);
  const uncatProds = products.filter(p => !p.category_id);
  const toggleCat  = (id) => setExpandedCats(p => ({ ...p, [id]: !p[id] }));
  const toggleProd = (id) => setExpandedProds(p => ({ ...p, [id]: !p[id] }));

  const renderProduct = (p) => {
    const avail       = (p.variants||[]).filter(v => v.unit_status === 'available');
    const recommended = getRecommended(p.variants||[]);
    const isExpanded  = expandedProds[p.id];
    return (
      <div key={p.id} style={{ marginBottom: 4 }}>
        <div
          style={{ display:'flex', alignItems:'center', gap:8, padding:'7px 10px', borderRadius:6, cursor:'pointer', background:'#0f0f1a', border:'1px solid #252840' }}
          onClick={() => toggleProd(p.id)}
        >
          {p.photo_urls?.[0]
            ? <img src={p.photo_urls[0]} alt="" style={{ width:32, height:32, objectFit:'cover', borderRadius:4, flexShrink:0 }} />
            : <div style={{ width:32, height:32, background:'#252840', borderRadius:4, display:'flex', alignItems:'center', justifyContent:'center', fontSize:16, flexShrink:0 }}>📦</div>
          }
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ fontSize:13, fontWeight:600, color:'#fff', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{p.name}</div>
            <div style={{ fontSize:11, color:'#555' }}>
              {avail.length}/{(p.variants||[]).length} available
              {p.base_price_day != null && <span style={{ color:'#4ade80', marginLeft:8 }}>${p.base_price_day}/day</span>}
            </div>
          </div>
          <span style={{ fontSize:11, color:'#444' }}>{isExpanded ? '▲' : '▼'}</span>
        </div>

        {isExpanded && (
          <div style={{ marginLeft:12, marginTop:4 }}>
            {(p.variants||[]).length === 0 && (
              <div style={{ fontSize:12, color:'#444', padding:'6px 10px', fontStyle:'italic' }}>No units added yet</div>
            )}
            {(p.variants||[]).map(v => {
              const isAvail       = v.unit_status === 'available';
              const isRecommended = recommended?.id === v.id;
              const isSelected    = selectedVariantIds.includes(v.id);
              const statusInfo    = STATUS_COLOR[v.unit_status] || STATUS_COLOR.available;
              return (
                <div
                  key={v.id}
                  onClick={() => isAvail && onSelect(p, v)}
                  style={{
                    display:'flex', alignItems:'center', gap:10,
                    padding:'8px 12px', borderRadius:6, marginBottom:3,
                    cursor: isAvail ? 'pointer' : 'not-allowed',
                    border:'1px solid',
                    borderColor: isSelected ? '#3b82f6' : isRecommended ? '#166534' : '#252840',
                    background:  isSelected ? '#1a2a4a' : isRecommended ? '#0d2010' : '#1a1c2e',
                    opacity: isAvail ? 1 : 0.5,
                  }}
                >
                  <div style={{ width:8, height:8, borderRadius:'50%', flexShrink:0, background: isAvail ? '#4ade80' : statusInfo.color }} />
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                      <span style={{ fontSize:13, color: isAvail?'#fff':'#555', fontWeight:600 }}>
                        {v.serial_number || v.sku || `Unit #${v.id.slice(-4)}`}
                      </span>
                      {isRecommended && <span style={{ fontSize:10, background:'#166834', color:'#4ade80', padding:'1px 6px', borderRadius:8, fontWeight:600 }}>Recommended</span>}
                      {isSelected    && <span style={{ fontSize:10, background:'#1e3a5f', color:'#60a5fa', padding:'1px 6px', borderRadius:8, fontWeight:600 }}>Selected</span>}
                    </div>
                    <div style={{ fontSize:11, color:'#555', marginTop:1 }}>
                      {v.condition && <span style={{ textTransform:'capitalize', marginRight:8 }}>{v.condition}</span>}
                      {v.hour_meter > 0 && <span style={{ marginRight:8 }}>{v.hour_meter} hrs</span>}
                      {!isAvail && <span style={{ color:statusInfo.color }}>{statusInfo.label}</span>}
                    </div>
                  </div>
                  {v.price_day_override != null && <span style={{ fontSize:11, color:'#4ade80' }}>${v.price_day_override}/day</span>}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:2000 }}>
      <div style={{ background:'#1a1c2e', border:'1px solid #2a2a4a', borderRadius:12, width:600, maxHeight:'80vh', display:'flex', flexDirection:'column', color:'#e0e0f0', fontFamily:'sans-serif' }}>
        <div style={{ padding:'16px 20px', borderBottom:'1px solid #252840', display:'flex', alignItems:'center', gap:12 }}>
          <div style={{ flex:1, fontSize:16, fontWeight:700, color:'#fff' }}>Select Equipment</div>
          <input
            autoFocus
            style={{ background:'#0f0f20', border:'1px solid #2a2a4a', borderRadius:6, padding:'7px 12px', color:'#e0e0f0', fontSize:13, width:220 }}
            placeholder="🔍  Search..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <button onClick={onClose} style={{ background:'none', border:'none', color:'#555', cursor:'pointer', fontSize:20, padding:'0 4px' }}>✕</button>
        </div>

        <div style={{ overflowY:'auto', padding:'14px 20px', flex:1 }}>
          {loading && <div style={{ color:'#555', textAlign:'center', padding:40 }}>Loading...</div>}

          {!loading && filteredProducts && (
            <div>
              <div style={{ fontSize:11, color:'#555', marginBottom:8 }}>{filteredProducts.length} results for "{search}"</div>
              {filteredProducts.map(p => renderProduct(p))}
              {filteredProducts.length === 0 && <div style={{ color:'#444', textAlign:'center', padding:30 }}>No equipment found.</div>}
            </div>
          )}

          {!loading && !filteredProducts && (
            <>
              {rootCats.map(cat => {
                const catProds = products.filter(p => p.category_id === cat.id);
                if (!catProds.length) return null;
                return (
                  <div key={cat.id} style={{ marginBottom:12 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:8, padding:'6px 4px', cursor:'pointer', borderBottom:'1px solid #252840', marginBottom:8 }} onClick={() => toggleCat(cat.id)}>
                      <span style={{ fontSize:14 }}>📦</span>
                      <span style={{ fontSize:13, fontWeight:700, color:'#a0a0ff', flex:1 }}>{cat.name}</span>
                      <span style={{ fontSize:11, color:'#444' }}>{catProds.length} products</span>
                      <span style={{ fontSize:11, color:'#444' }}>{expandedCats[cat.id] ? '▲' : '▼'}</span>
                    </div>
                    {expandedCats[cat.id] && catProds.map(p => renderProduct(p))}
                  </div>
                );
              })}
              {uncatProds.length > 0 && (
                <div style={{ marginBottom:12 }}>
                  <div style={{ fontSize:12, color:'#555', fontStyle:'italic', marginBottom:8, borderBottom:'1px solid #252840', paddingBottom:6 }}>Uncategorized</div>
                  {uncatProds.map(p => renderProduct(p))}
                </div>
              )}
            </>
          )}
        </div>

        <div style={{ padding:'12px 20px', borderTop:'1px solid #252840', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div style={{ fontSize:12, color:'#555' }}>Green = recommended · Click a unit to add it</div>
          <button onClick={onClose} style={{ background:'#1a1a3a', border:'1px solid #2a2a4a', color:'#888', borderRadius:6, padding:'7px 16px', fontSize:13, cursor:'pointer' }}>Done</button>
        </div>
      </div>
    </div>
  );
}
